import React, { useState } from "react";
import { Alert, Button, Form, Input } from "antd";
import { ArrowLeftOutlined, CheckOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

import { API_URL_PASSWORD_RESET } from "../../config/config";
import { AUTH } from "../../config/lang";
import AuthShell, { useAuthLang, useClearErrorsOnLangChange } from "./AuthShell";

/* Deliberately permissive about formatting (spaces, dashes, parens) and only checks for a
   plausible digit count — authoritative validation is the server's job. */
const isPhone = value => {
  if (!/^\+?[\d\s()\-]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, "").length;
  return digits >= 9 && digits <= 15;
};

function requestPasswordReset(phone_number) {
  if (!API_URL_PASSWORD_RESET) return Promise.reject(new Error("NOT_CONFIGURED"));

  return fetch(API_URL_PASSWORD_RESET, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    mode: "cors",
    body: JSON.stringify({ phone_number }),
  }).then(response => {
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json().catch(() => ({}));
  });
}

function BackToSignIn({ t }) {
  return (
    <div className="auth-backrow">
      <Link className="auth-link auth-link--back" to="/login">
        <ArrowLeftOutlined aria-hidden="true" />
        <span>{t("back_link")}</span>
      </Link>
    </div>
  );
}

export default function ForgotPassword() {
  const { lang, setLang, t } = useAuthLang();
  const [form] = Form.useForm();
  useClearErrorsOnLangChange(form, lang);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState(null);
  /* Holds the number the request was made for. Non-null means "show the confirmation" — the
     number is needed to render it, so the two can't drift apart. */
  const [sentTo, setSentTo] = useState(null);

  const submit = ({ phone_number }) => {
    const phone = phone_number.trim();
    setFailure(null);
    setLoading(true);

    requestPasswordReset(phone)
      .then(() => setSentTo(phone))
      .catch(err => {
        /* Never claim a message was sent when it wasn't. Unconfigured and failed look the same
           to the user — both mean "can't do this right now" — but only one is worth logging.
           Either way this beats the dead button that shipped before. */
        if (err.message === "NOT_CONFIGURED") console.warn("API_PASSWORD_RESET is not set; password reset cannot be requested.");
        setFailure(t("err_reset_unavailable"));
      })
      .finally(() => setLoading(false));
  };

  if (sentTo) {
    return (
      <AuthShell lang={lang} setLang={setLang} t={t}>
        <div className="auth-form auth-form--sent">
          <div className="auth-sent__icon">
            <CheckOutlined aria-hidden="true" />
          </div>
          <div className="auth-form__head">
            <h2 className="auth-title">{t("sent_title")}</h2>
            {/* Phrase interpolation, so word order can differ per locale */}
            <p className="auth-subtitle">{(t("sent_sub") || "").replace("{phone}", sentTo)}</p>
          </div>
          <BackToSignIn t={t} />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell lang={lang} setLang={setLang} t={t}>
      <Form form={form} className="auth-form" layout="vertical" requiredMark={false} onFinish={submit} validateTrigger="onChange">
        <div className="auth-form__head">
          <h2 className="auth-title">{t("forgot_title")}</h2>
          <p className="auth-subtitle">{t("forgot_sub")}</p>
        </div>

        <div className="auth-fields">
          <Form.Item
            name="phone_number"
            label={t("phone_label")}
            rules={[
              { required: true, whitespace: true, message: t("err_required") },
              {
                validator: (_, value) => (!value || isPhone(value.trim()) ? Promise.resolve() : Promise.reject(new Error(t("err_phone")))),
              },
            ]}>
            <Input size="large" type="tel" autoComplete="tel" autoFocus placeholder={t("phone_ph")} onChange={() => setFailure(null)} />
          </Form.Item>
        </div>

        <div className="auth-actions">
          <Button className="auth-submit" type="primary" size="large" htmlType="submit" loading={loading}>
            {t("send_btn")}
          </Button>

          {failure ? <Alert className="auth-alert" type="warning" showIcon title={failure} role="alert" /> : null}

          <BackToSignIn t={t} />
        </div>
      </Form>
    </AuthShell>
  );
}
